<template lang="pug">
.AppContent(:class="{'is-end': inView}")
  slot
  .AppContent-footer(ref="contentEnd")
</template>

<style lang="stylus">
$columns = 12

.AppContent
  font-size: unitRes(12, 24)
  font-weight: 400
  line-height: 1.6
  transform-origin: bottom
  transition: 0.5s
  margin-left: -($outerMargin)
  margin-right: -($outerMargin)

  +mq($until: 'tablet')
    margin-left: -($outerMargin-sm)
    margin-right: -($outerMargin-sm)

  &.is-end
    box-shadow: rgba(0, 0, 0, 0.1) 0px 0 160px 80px
    transform: translateY(-60px) scale(0.84)

  &.is-normal
    transform: none
    box-shadow: none
    margin-right: 0
    margin-left: 0

  &.is-books
    font-family: 'Inconsolata', monospace

  &.is-technology
    font-family: 'Roboto Mono', monospace

  &.is-business
    font-family: 'Zilla Slab', serif
    font-weight: 300

  &.is-design
    font-family: 'Eczar', serif

  &.is-code
    font-family: 'IBM Plex Mono', monospace

  p
    line-height: 1.6

  &.is-small
    font-size: $size-small

  &.is-large
    $size-large

  li
    font-weight: inherit

  li + li
    margin-top: 0.5em

  p, dl, ol, ul, blockquote, pre, table
    font-size: 1em

    &:not(:last-child)
      margin-bottom: 1em

  h1
    font-size: 4em
    margin-bottom: 0.5em

    &:not(:first-child)
      margin-top: 1em

  h2
    font-size: 3.2em
    margin-bottom: 0.5714em

    &:not(:first-child)
      margin-top: 1.1428em

  h3
    font-size: 2.4em
    margin-bottom: 0.6666em

    &:not(:first-child)
      margin-top: 1.8em

  h4
    font-size: 2em
    margin-bottom: 0.8em

    &:not(:first-child)
      margin-top: 1.8em

  h5
    font-size: 1.6em
    margin-bottom: 0.8888em

    &:not(:first-child)
      margin-top: 1.8em

  h6
    font-size: 1em
    margin-bottom: 1em

    &:not(:first-child)
      margin-top: 1.8em

  blockquote
    border-left: 0.5em solid $clPrimary
    padding: 10px 0 10px 20px
    margin: 0

  .align-quote
    cite
      float: right
      font-size: 0.6em
      margin-right: 10%
      color: $clSecondary

    &:not(:last-child)
      margin-bottom: 1em

  ol
    list-style: decimal inside
    margin: 1em 0 0
    padding: 0

    ul, ol
      margin-left: 1em

  ul
    list-style: disc outside
    margin: 1em 0 0 1em
    padding: 0

    ul, ol
      margin-left: 1em

    ul
      list-style-type: circle
      margin-top: 0.5em

      ul
        list-style-type: square

  dd
    margin-left: 2em

  figure
    margin-left: 0
    margin-right: 0
    text-align: center

    &:not(:first-child)
      margin-top: 2em

    &:not(:last-child)
      margin-bottom: 2em

    &.is-active
      img
        box-shadow: 0 0 25px alpha($clBlack, 5%)

    &.is-center
      max-width: 500px
      margin: 0 auto

    &.is-floatLeft
      float: left
      margin-right: 40px
      max-width: 500px
      width: 50vw

      &:after
        display: block
        clear: both
        content: ''

    &.is-floatRight
      float: right
      margin-left: 40px
      max-width: 500px
      width: 50vw

      &:after
        display: block
        clear: both
        content: ''

    &.is-full
      width: 100%
      max-width: 100%

    img
      display: inline-block
      max-width: 100%
      height: auto

    figcaption
      font-style: italic

  pre
    overflow-x: auto
    padding: $padding
    white-space: pre
    word-wrap: normal

  sup, sub
    font-size: 75%

  .align-section
    max-width: 800px
    margin: 0 auto 0
    position: relative
    z-index: 1
    overflow: hidden

    +mq($until: 900px)
      padding-left: $outerMargin
      padding-right: $outerMargin

      +mq($until: 'tablet')
        padding-left: $outerMargin-sm
        padding-right: $outerMargin-sm

    &.is-medium
      max-width: 1200px

      +mq($until: 1200px)
        padding-left: $outerMargin
        padding-right: $outerMargin

      +mq($until: 'tablet')
        padding-left: $outerMargin-sm
        padding-right: $outerMargin-sm

    &.is-full
      max-width: 100%
      margin-right: $outerMargin
      margin-left: $outerMargin

      +mq($until: 'tablet')
        margin-right: $outerMargin-sm
        margin-left: $outerMargin-sm

    &.is-image
      background-position: center center
      background-size: cover
      background-attachment: fixed
      min-height: 300px

    &.is-max
      width: 100%
      max-width: 100vw
      padding-left: 0
      padding-right: 0

    &.has-bgImage, &.has-bgColor
      padding: unitRes(20, 40)

  iframe
    border: 0
    max-width: 100%
    height: auto

  .align-figure
    &.is-full
      width: 100%
      height: auto

      img
        width: 100%
        height: auto
        display: block

  .align-line
    margin: 1em auto
    border: 0
    height: 4px
    background: transparent
    border-top: 1px solid $clBlack

    &.is-dashed
      border-top-style: dashed

    &.is-dotted
      border-top-style: dotted

    &.is-double
      border-bottom: 1px solid $clBlack

    &.is-bold
      border-top-width: 2px

    &.is-dots
      border-top-style: dotted
      border-top-width: 5px
      width: 25px

  .align-iframe, .align-video
    position: relative
    padding-bottom: 56.25%
    height: 0

    &:not(:last-child)
      margin-bottom: 1em

    iframe
      position: absolute
      top: 0
      right: 0
      left: 0
      bottom: 0
      height: 100%
      width: 100%

  .align
    &-grid
      display: flex
      flex-wrap: wrap
      margin: 0 -20px

      &.is-center
        justify-content: center

      &.is-shrink
        >.aling-column:not([class*='is-'])
          flex: 0 0 auto

      &.is-relaxed
        margin-right: 0
        margin-left: 0

    &-column
      padding: 20px
      flex: 1

      for $n in (0 .. $columns)
        &.is-{$n}
          flex: 0 0 ($n / $columns * 100) %
          min-width: ($n / $columns * 100) %

    &-gallery
      display: flex
      flex-wrap: wrap

      &-image
        object-fit: cover
        flex-basis: 33%
        flex-grow: 1
        flex-shrink: 0
        height: 200px
        margin: 5px
        border-radius: 0

    &-bgVideo, &-bgImage
      position: absolute
      overflow: hidden
      top: 0
      left: 0
      right: 0
      bottom: 0
      background-repeat: no-repeat
      background-size: cover
      background-position: center center
      width: 100%
      height: 100%
      opacity: 0.5
      object-fit: cover
      z-index: -1

      &.is-parallax
        background-attachment: fixed

    &-bgVideo
      video
        width: 100%
        height: 100%
        object-fit: cover

  a
    color: $clPrimary

    &:hover
      color: $clSecondary

  &.is-help
    .align-section
      max-width: 100% !important

      +mq($until: 900px)
        padding-left: 0
        padding-right: 0

/* PrismJS 1.10.0
http://prismjs.com/download.html?themes=prism-okaidia&languages=markup+css+clike+javascript+pug+stylus */
/**
 * okaidia theme for JavaScript, CSS and HTML
 * Loosely based on Monokai textmate theme by http://www.monokai.nl/
 * @author ocodia
 */
code[class*='language-'], pre[class*='language-']
  color: #ccc
  background: none
  text-shadow: 0 1px rgba(0, 0, 0, 0.3)
  font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace
  text-align: left
  white-space: pre
  word-spacing: normal
  word-break: normal
  word-wrap: normal
  line-height: 1.5
  -webkit-hyphens: none
  -moz-hyphens: none
  -ms-hyphens: none
  hyphens: none

/* Code blocks */
pre[class*='language-']
  padding: unitRes(20, 40)
  overflow: auto
  border-radius: 0

:not(pre) > code[class*='language-'], pre[class*='language-']
  background: $clBlack

/* Inline code */
:not(pre) > code[class*='language-']
  padding: 0
  border-radius: 0
  white-space: normal

.token.comment, .token.prolog, .token.doctype, .token.cdata
  color: $clPurple

.token.punctuation
  color: #f8f8f2

.namespace
  opacity: 0.7

.token.property, .token.tag, .token.constant, .token.symbol, .token.deleted
  color: $clWarning

.token.boolean, .token.number
  color: $clPurple

.token.selector, .token.attr-name, .token.string, .token.char, .token.builtin, .token.inserted
  color: $clInfo

.token.operator, .token.entity, .token.url, .language-css .token.string, .style .token.string, .token.variable
  color: #f8f8f2

.token.atrule, .token.attr-value, .token.function
  color: $clPrimary

.token.keyword
  color: #66d9ef

.token.regex, .token.important
  color: $clWarning

.token.important, .token.bold
  font-weight: bold

.token.italic
  font-style: italic

.token.entity
  cursor: help



.align-imagegrid{
  display: flex;
  flex-wrap: wrap;
  margin: 0 -2px -4px;
  max-width: none;
  padding: 0;
  width: calc(100% + 2px * 2);
  &-col{
    box-sizing: content-box !important;
    display: inline-block;
    margin: 0 2px 4px;
    position: relative;
    overflow: hidden;
    background: #f6f6f6;
    &-ratio{
      display: block;
    }
    img{
      position: absolute;
      top: 0;
      vertical-align: bottom;
      width: 100%;
    }
  }
}
</style>

<script>
export default {
  name: 'Content',
  props: {
    highlight: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    inView: false
  }),
  mounted() {
    if (!this.highlight) return;

    import('prismjs').then(Prism => {
      Prism.highlightAllUnder(this.$el);
    });
    if (window.pageYOffset >= 600) {
      const observer = new IntersectionObserver(
        entries => {
          this.inView = entries[0].intersectionRatio > 0;
        },
        { root: null }
      );
      // start observing this image.
      observer.observe(this.$refs.contentEnd);
    }
  }
};
</script>
